import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import { crmApi } from '@/api';
import { CustomerSelect } from '@/components/selects';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { useDebounce } from '@/hooks/useDebounce';
import { usePagination } from '@/hooks/usePagination';
import { usePageAccess } from '@/hooks/usePageAccess';
import type { CrmConvertLeadRequest, CrmLeadRequest, CrmLeadResponse } from '@/types/crm';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

const emptyLead: CrmLeadRequest = { leadStatus: 'NEW', leadScore: 0 };

export function CrmLeadsPage() {
  const { canCreate, canEdit } = usePageAccess();
  const pagination = usePagination();
  const debouncedSearch = useDebounce(pagination.search);
  const [data, setData] = useState<CrmLeadResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [convertLead, setConvertLead] = useState<CrmLeadResponse | null>(null);
  const [form, setForm] = useState<CrmLeadRequest>(emptyLead);
  const [customerId, setCustomerId] = useState('');
  const [convertForm, setConvertForm] = useState<CrmConvertLeadRequest>({
    dealName: '',
    expectedRevenue: 0,
    probabilityPercentage: 25,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await crmApi.listLeads({
        page: pagination.page,
        size: pagination.size,
        search: debouncedSearch || undefined,
      });
      setData(res.content);
      pagination.applyPageResponse(res);
    } catch (err) {
      showApiError(err, 'Failed to load CRM leads');
    } finally {
      setLoading(false);
    }
  }, [pagination, debouncedSearch]);

  useEffect(() => {
    load();
  }, [load]);

  const columns: Column<CrmLeadResponse>[] = [
    { key: 'id', header: 'Lead #' },
    {
      key: 'customerName',
      header: 'Customer',
      render: (row) =>
        row.customerId ? (
          <Link to={`/crm/customers/${row.customerId}`} className="text-primary-600 hover:underline">
            {row.customerName || `Customer #${row.customerId}`}
          </Link>
        ) : (
          row.customerName || '—'
        ),
    },
    { key: 'leadSource', header: 'Source' },
    { key: 'leadStatus', header: 'Status' },
    { key: 'leadScore', header: 'Score' },
    {
      key: 'converted',
      header: 'Converted',
      render: (row) =>
        row.converted ? (
          row.convertedDealId ? (
            <Link to="/crm/deals" className="text-primary-600 text-xs hover:underline">
              Deal #{row.convertedDealId}
            </Link>
          ) : (
            'Yes'
          )
        ) : canEdit ? (
          <button
            type="button"
            className="text-primary-600 text-xs hover:underline"
            onClick={() => {
              setConvertLead(row);
              setConvertForm({
                dealName: `Deal from lead #${row.id}`,
                expectedRevenue: row.expectedRevenue ?? 0,
                probabilityPercentage: 25,
                customerName: row.customerName,
              });
            }}
          >
            Convert
          </button>
        ) : (
          '—'
        ),
    },
  ];

  const createLead = async () => {
    setSaving(true);
    try {
      await crmApi.createLead({
        ...form,
        customerId: customerId ? Number(customerId) : undefined,
      });
      notify.success('Lead created');
      setOpen(false);
      setForm(emptyLead);
      setCustomerId('');
      load();
    } catch (err) {
      showApiError(err, 'Lead creation failed');
    } finally {
      setSaving(false);
    }
  };

  const submitConvert = async () => {
    if (!convertLead || !convertForm.dealName.trim()) return;
    setSaving(true);
    try {
      await crmApi.convertLead(convertLead.id, {
        ...convertForm,
        customerName: convertLead.customerId ? undefined : convertForm.customerName,
      });
      notify.success('Lead converted — deal created');
      setConvertLead(null);
      load();
    } catch (err) {
      showApiError(err, 'Lead conversion failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Leads"
        description="Manage lead intake, qualification and conversion"
        breadcrumbs={[{ label: 'Org', to: '/org' }, { label: 'CRM', to: '/crm' }, { label: 'Leads' }]}
        actions={
          canCreate ? (
            <Button onClick={() => setOpen(true)}>
              <AddIcon fontSize="small" /> New lead
            </Button>
          ) : undefined
        }
      />
      <DataTable
        columns={columns}
        data={data}
        keyField="id"
        loading={loading}
        search={pagination.search}
        onSearchChange={(v) => {
          pagination.setSearch(v);
          pagination.resetPage();
        }}
        page={pagination.page}
        totalPages={pagination.totalPages}
        totalElements={pagination.totalElements}
        onPageChange={pagination.setPage}
        emptyTitle="No leads yet"
        emptyDescription="Capture leads from campaigns or add them manually to start your pipeline."
        emptyActionLabel={canCreate ? 'New lead' : undefined}
        onEmptyAction={canCreate ? () => setOpen(true) : undefined}
      />
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Create lead"
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={createLead} loading={saving}>
              Save
            </Button>
          </>
        }
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <CustomerSelect value={customerId} onChange={setCustomerId} />
          <Input
            label="Lead source"
            value={form.leadSource ?? ''}
            onChange={(e) => setForm({ ...form, leadSource: e.target.value })}
          />
          <Input
            label="Lead status"
            value={form.leadStatus ?? 'NEW'}
            onChange={(e) => setForm({ ...form, leadStatus: e.target.value })}
          />
          <Input
            label="Lead score"
            type="number"
            value={(form.leadScore ?? 0).toString()}
            onChange={(e) => setForm({ ...form, leadScore: Number(e.target.value) || 0 })}
          />
        </div>
      </Modal>

      <Modal
        open={convertLead != null}
        onClose={() => setConvertLead(null)}
        title={`Convert lead #${convertLead?.id ?? ''}`}
        footer={
          <>
            <Button variant="secondary" onClick={() => setConvertLead(null)}>
              Cancel
            </Button>
            <Button onClick={() => void submitConvert()} loading={saving}>
              Convert to deal
            </Button>
          </>
        }
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Deal name"
            value={convertForm.dealName}
            onChange={(e) => setConvertForm({ ...convertForm, dealName: e.target.value })}
          />
          <Input
            label="Expected revenue"
            type="number"
            value={String(convertForm.expectedRevenue ?? 0)}
            onChange={(e) => setConvertForm({ ...convertForm, expectedRevenue: Number(e.target.value) || 0 })}
          />
          {!convertLead?.customerId && (
            <>
              <Input
                label="Customer name"
                value={convertForm.customerName ?? ''}
                onChange={(e) => setConvertForm({ ...convertForm, customerName: e.target.value })}
              />
              <Input
                label="Customer email"
                type="email"
                value={convertForm.customerEmail ?? ''}
                onChange={(e) => setConvertForm({ ...convertForm, customerEmail: e.target.value })}
              />
            </>
          )}
        </div>
      </Modal>
    </div>
  );
}
